import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { User, UserService } from '@pwc-bei/common';
import {
  AuthenticationService,
  CryptoService,
  PasswordStrengthService,
} from '@pwc-bei/security';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { MustMatch } from '../../../../helpers/must-match.validator';
import { ToastUtils } from '../../../../utils/toast-utils';
import { BasePage } from '../../../base-page/base-page';
import { ProfileModule } from '../../profile.module';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { ProfileService } from '../../services/profile.service';
import { RoutePath } from '../../../../enums/route-path.enum';
import { Router } from '@angular/router';
import { piInfo, PwcIconsLibrary } from '@pwc-bei/icons';

@Component({
    selector: 'app-change-password',
    templateUrl: './change-password.component.html',
    styleUrls: ['./change-password.component.scss'],
    providers: [CryptoService],
    standalone: false
})
export class ChangePasswordComponent implements OnInit {
  faChevronLeft = faChevronLeft;
  form: FormGroup;
  user: User = new User();
  loading: boolean = false;
  submitted: boolean = false;
  messages: any;

  constructor(
    private userService: UserService,
    private profileService: ProfileService,
    private authenticationService: AuthenticationService,
    private translateService: TranslateService,
    private formBuilder: FormBuilder,
    private cryptoService: CryptoService,
    private passwordStrengthService: PasswordStrengthService,
    private router: Router,
    private toastr: ToastrService,
    private library: PwcIconsLibrary,
  ) {
    this.library.registerIcons([piInfo]);
  }

  ngOnInit(): void {
    this.loading = true;
    this.user = new User();

    this.initValidation();

    this.authenticationService.dataUser().subscribe({
      next: (user: User) => {
        this.user = { ...user };
        this.initValidation();
        this.loading = false;
      }, error: (error) => {
        // console.error(error);
        // this.openAlert(0, 'error', this.messages.errorLoading);
        this.loading = false;
      },
    });
  }

  private initValidation(): void {
    this.form = this.formBuilder.group(
      {
        oldPassword: ['', Validators.required],
        password: ['', [Validators.required, Validators.minLength(16)]],
        confirmPassword: ['', Validators.required],
      },
      {
        validator: MustMatch('password', 'confirmPassword'),
      },
    );
  }

  get f() {
    return this.form.controls;
  }

  onSave(): void {
    this.submitted = true;
    if (this.form.invalid) {
      // console.warn('Form invalid');
      return;
    }

    const password = this.f.password.value;
    if (password !== '') {
      //  Check password strength.
      const strength = this.passwordStrengthService.checkPassword(password);
      if (strength != null && strength.score < 3) {
        this.toastr.warning(
          this.translateService.instant(
            'generic.message.validation.weak-password',
          ),
          ToastUtils.TITLE_ERROR,
        );
        return;
      }
    }

    this.loading = true;
    this.user.password = this.cryptoService.encryptToString(password);
    if (password === '') {
      delete this.user.password;
    }
    // ----

    this.loading = true;
    const request = {
      oldPassword: this.cryptoService.encryptToString(this.f.oldPassword.value),
      password: this.cryptoService.encryptToString(password),
    };
    this.user.password = this.cryptoService.encryptToString(password);
    if (password === '') {
      return;
    }

    // this.userService.changePassword(request)
    //   .subscribe((res: User) => {
    //     this.user = {...res};
    //     this.openAlert(0, 'success', this.messages.savingSuccess);
    //     this.loading = false;
    //   }, err => {
    //     // console.error(err);
    //     if (err.status === 422 && err.error.type === 'unique_constraint_violated') {
    //       const msg = this.translateService.instant('validation.password.history');
    //       this.openAlert(0, 'error', msg);
    //     } else if (err.status === 422 && err.error.type === 'invalid') {
    //       const msg = this.translateService.instant('validation.password.invalid');
    //       this.openAlert(0, 'error', msg);
    //     } else {
    //       this.openAlert(0, 'error', this.messages.errorSaving);
    //     }
    //     this.loading = false;
    //   });

    // ---
    this.profileService.changePassword(request).subscribe({
      next: (res: User) => {
        this.user = { ...res };
        this.showSaveSuccess();
        this.loading = false;
      },
      error: (err) => {
        console.log('changePassword', err);
        if (
          err.status === 422 &&
          err.error.type === 'unique_constraint_violated'
        ) {
          this.toastr.error(
            this.translateService.instant('login.error.password-unique'),
            ToastUtils.TITLE_ERROR,
          );
        } else if (
          err.status === 422 &&
          err.error.type === 'invalid' &&
          err.error.details &&
          err.error.details.property &&
          err.error.details.property === 'oldPassword'
        ) {
          this.toastr.error(
            this.translateService.instant('login.error.invalid-old-password'),
            ToastUtils.TITLE_ERROR,
          );
        } else if (err.status === 422 && err.error.type === 'invalid') {
          this.toastr.error(
            this.translateService.instant('login.error.invalid-password'),
            ToastUtils.TITLE_ERROR,
          );
        } else {
          this.toastr.error(
            this.translateService.instant('login.error.updating-password'),
            ToastUtils.TITLE_ERROR,
          );
        }

        this.loading = false;
      },
    });
  }

  showSaveSuccess() {
    this.toastr.success(this.translateService.instant('login.alert.success-change-psw'), null);
  }

  backToHomepage(): void {
    this.router.navigate([RoutePath.HOME]);
  }
}
